import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import avatar from 'flarum/common/helpers/avatar';
import username from 'flarum/common/helpers/username';
import Stream from 'flarum/common/utils/Stream';

import { apiUrl, toSummary, updateClubSummary } from '../../common/clubs';

/**
 * Day-to-day club running for its president and vices: join requests, the
 * cover photo and the description. Adding or removing members by hand stays
 * in byobu's own recipient modal.
 *
 * Attrs: club (summary)
 */
export default class ClubLeaderModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    this.club = this.attrs.club;
    this.requests = [];
    this.loadingRequests = true;
    this.busy = {};
    this.description = Stream('');

    this.load();
  }

  className() {
    return 'ClubLeaderModal Modal--small';
  }

  /**
   * Core focuses and selects the first text field on open; here that would
   * select the whole description while the leader came to answer requests.
   */
  onready() {}

  title() {
    return app.translator.trans('happydigital-clubs.forum.leader.title', { club: this.club.name });
  }

  load() {
    app
      .request({ method: 'GET', url: apiUrl('/' + this.club.id + '/requests') })
      .then((response) => this.apply(response.data))
      .catch(() => {})
      .then(() => {
        this.loadingRequests = false;
        m.redraw();
      });
  }

  /**
   * @param {{club: object, requests: Array}} data
   */
  apply(data) {
    if (data.club) {
      this.club = data.club;
      this.description(data.club.description || '');
      updateClubSummary(data.club.id, toSummary(data.club));
    }

    if (data.requests) {
      data.requests.forEach((request) => this.pushUser(request.user));
      this.requests = data.requests;
    }
  }

  pushUser(user) {
    app.store.pushObject({
      type: 'users',
      id: user.id,
      attributes: { username: user.username, displayName: user.displayName, avatarUrl: user.avatarUrl, slug: user.slug },
    });
  }

  content() {
    return (
      <div className="Modal-body">
        <div className="Form-group">
          <label>{app.translator.trans('happydigital-clubs.forum.leader.requests_heading', { count: this.requests.length })}</label>
          {this.requestsList()}
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('happydigital-clubs.forum.leader.cover_label')}</label>
          <div className="ClubLeaderModal-buttons">
            {Button.component(
              { className: 'Button', icon: 'fas fa-image', loading: !!this.busy.cover, onclick: () => this.chooseCover() },
              app.translator.trans(this.club.coverUrl ? 'happydigital-clubs.forum.leader.change_cover' : 'happydigital-clubs.forum.leader.upload_cover')
            )}
            {this.club.coverUrl
              ? Button.component(
                  { className: 'Button', icon: 'fas fa-times', disabled: !!this.busy.cover, onclick: () => this.removeCover() },
                  app.translator.trans('happydigital-clubs.forum.leader.remove_cover')
                )
              : null}
          </div>
          <div className="helpText">{app.translator.trans('happydigital-clubs.forum.leader.cover_help')}</div>
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('happydigital-clubs.forum.leader.mention_label')}</label>
          <div className="helpText">{app.translator.trans('happydigital-clubs.forum.leader.mention_help', { mention: '@' + this.club.slug })}</div>
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('happydigital-clubs.forum.leader.description_label')}</label>
          <textarea className="FormControl" rows="3" maxlength="1000" bidi={this.description} disabled={!!this.busy.description} />
          {Button.component(
            { className: 'Button ClubLeaderModal-save', loading: !!this.busy.description, onclick: () => this.saveDescription() },
            app.translator.trans('happydigital-clubs.forum.leader.save')
          )}
        </div>
      </div>
    );
  }

  requestsList() {
    if (this.loadingRequests) return <LoadingIndicator size="small" display="inline" />;

    if (!this.requests.length) {
      return <p className="helpText">{app.translator.trans('happydigital-clubs.forum.leader.no_requests')}</p>;
    }

    return (
      <ul className="ClubLeaderModal-requests">
        {this.requests.map((request) => {
          const user = app.store.getById('users', request.user.id);
          const key = 'request-' + request.user.id;

          if (!user) return null;

          return (
            <li key={request.user.id} className="ClubLeaderModal-request">
              <Link href={app.route.user(user)} className="ClubLeaderModal-person">
                {avatar(user)}
                <span className="ClubLeaderModal-name">{username(user)}</span>
              </Link>
              {Button.component(
                {
                  className: 'Button Button--primary',
                  loading: this.busy[key] === 'approve',
                  disabled: !!this.busy[key],
                  onclick: () => this.answer(request.user.id, true),
                },
                app.translator.trans('happydigital-clubs.forum.leader.approve')
              )}
              {Button.component(
                {
                  className: 'Button',
                  loading: this.busy[key] === 'reject',
                  disabled: !!this.busy[key],
                  onclick: () => this.answer(request.user.id, false),
                },
                app.translator.trans('happydigital-clubs.forum.leader.reject')
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  /**
   * Run a request, tracking a busy flag; responses carrying club data update
   * the modal and the forum payload.
   */
  act(key, value, options, onDone) {
    this.busy[key] = value;

    return app
      .request(options)
      .then((response) => {
        const data = response.data || {};

        this.apply(data.club ? data : { club: data });

        if (onDone) onDone();
      })
      .catch(() => {})
      .then(() => {
        delete this.busy[key];
        m.redraw();
      });
  }

  answer(userId, approve) {
    const key = 'request-' + userId;
    const clearRequest = { method: 'DELETE', url: apiUrl('/' + this.club.id + '/requests/' + userId) };

    if (!approve) {
      this.act(key, 'reject', clearRequest);

      return;
    }

    // Approving adds the requester through byobu's own discussion save, the
    // same request "Edit recipients" sends, and then clears the request. The
    // server-side approve endpoint failed on the live forum while this path
    // works there, so the endpoint is no longer used from here.
    this.busy[key] = 'approve';

    this.addRecipient(userId)
      .then(() => this.act(key, 'approve', clearRequest, () => this.refreshDiscussion()))
      .catch(() => {
        delete this.busy[key];
        m.redraw();
      });
  }

  /**
   * Save the club discussion with the requester appended to its current
   * recipients. Every existing user and group is sent back, because byobu
   * treats anyone missing from the payload as removed.
   *
   * @param {string} userId
   * @returns {Promise}
   */
  addRecipient(userId) {
    const discussionId = String(this.club.discussionId);
    const loaded = app.store.getById('discussions', discussionId);

    return (loaded ? Promise.resolve(loaded) : app.store.find('discussions', discussionId)).then((discussion) => {
      const user = app.store.getById('users', String(userId));
      const users = (discussion.recipientUsers() || []).filter(Boolean);
      const groups = (discussion.recipientGroups() || []).filter(Boolean);

      if (!user || users.some((recipient) => recipient.id() === user.id())) return discussion;

      return discussion.save({ relationships: { recipientUsers: users.concat([user]), recipientGroups: groups } });
    });
  }

  saveDescription() {
    this.act('description', true, {
      method: 'PATCH',
      url: apiUrl('/' + this.club.id),
      body: { data: { attributes: { description: this.description() } } },
    });
  }

  chooseCover() {
    const input = document.createElement('input');

    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files && input.files[0];

      if (!file) return;

      const body = new FormData();
      body.append('cover', file);

      this.act('cover', true, { method: 'POST', url: apiUrl('/' + this.club.id + '/cover'), serialize: (raw) => raw, body });
    };

    input.click();
  }

  removeCover() {
    this.act('cover', true, { method: 'DELETE', url: apiUrl('/' + this.club.id + '/cover') });
  }

  /**
   * An approval adds a recipient and a "recipients changed" post through
   * byobu, so reload the discussion to show both.
   */
  refreshDiscussion() {
    app.store.find('discussions', String(this.club.discussionId)).catch(() => {});

    if (app.current && app.current.get('stream')) {
      app.current.get('stream').update();
    }
  }
}
